import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const StoreContext = createContext(null);
const API = window.location.origin + '/api';

const BRANCH_KEY = (slug) => `selectedBranch:${slug}`;

export function StoreProvider({ children }) {
  const [supermarkets, setSupermarkets] = useState([]);
  const [selectedStore, setSelectedStoreState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('selectedStore')) || null; } catch { return null; }
  });
  const [selectedBranch, setSelectedBranchState] = useState(() => {
    try {
      const store = JSON.parse(localStorage.getItem('selectedStore'));
      if (!store?.slug) return null;
      return JSON.parse(localStorage.getItem(BRANCH_KEY(store.slug))) || null;
    } catch { return null; }
  });

  useEffect(() => {
    axios.get(`${API}/supermarkets`)
      .then(r => setSupermarkets(r.data))
      .catch(error => console.error('Failed to fetch supermarkets:', error.message));
  }, []);

  const setSelectedStore = useCallback((store) => {
    setSelectedStoreState(store);
    if (store) {
      localStorage.setItem('selectedStore', JSON.stringify(store));
      // Restore previously chosen branch for this store, if any
      try {
        const saved = JSON.parse(localStorage.getItem(BRANCH_KEY(store.slug)));
        setSelectedBranchState(saved || null);
      } catch { setSelectedBranchState(null); }
    } else {
      localStorage.removeItem('selectedStore');
      setSelectedBranchState(null);
    }
  }, []);

  const setSelectedBranch = useCallback((branch) => {
    setSelectedBranchState(branch);
    const slug = selectedStore?.slug;
    if (!slug) return;
    if (branch) {
      localStorage.setItem(BRANCH_KEY(slug), JSON.stringify(branch));
    } else {
      localStorage.removeItem(BRANCH_KEY(slug));
    }
  }, [selectedStore]);

  const clearStore = useCallback(() => {
    setSelectedStoreState(null);
    setSelectedBranchState(null);
    localStorage.removeItem('selectedStore');
  }, []);

  const value = useMemo(() => ({
    supermarkets, selectedStore, selectedBranch, setSelectedStore, setSelectedBranch, clearStore
  }), [supermarkets, selectedStore, selectedBranch, setSelectedStore, setSelectedBranch, clearStore]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
